import { DestroyRef, Directive, ElementRef, NgZone, inject } from '@angular/core';

/**
 * VISUAL VIEWPORT
 * ---------------
 * For an element fixed to an edge of the screen: the nav bar, the staff Save bar.
 *
 * `position: fixed` places an element against the *layout* viewport. Pinch-
 * zooming — a laptop touchpad is enough — magnifies only the *visual*
 * viewport, and scrolling then pans that magnified view around inside the
 * layout viewport before the page itself moves. Anything fixed stays where
 * the unzoomed page would show it: a header slides up out of sight as soon as
 * you scroll down, and a bar fixed to the bottom sits partly below the screen.
 * The same gap opens on a phone when the on-screen keyboard comes up.
 *
 * This hands the element custom properties describing the visible area, in
 * layout-viewport pixels, for its CSS to position against:
 *   --vv-top     distance from the layout top to the visible top edge
 *   --vv-left    distance from the layout left to the visible left edge
 *   --vv-width   width of the visible area
 *   --vv-centre  horizontal centre of the visible area
 *   --vv-bottom  gap between the visible bottom edge and the layout bottom
 *
 * With no zoom they match the plain viewport, so nothing moves. The styles
 * are written straight to the element, outside Angular: this is pure layout
 * and needs no change detection.
 */
@Directive({
  selector: '[appVisualViewport]',
  standalone: true,
})
export class VisualViewportDirective {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const style = this.element.nativeElement.style;
    const update = () => {
      const layoutHeight = document.documentElement.clientHeight;
      style.setProperty('--vv-top', `${viewport.offsetTop}px`);
      style.setProperty('--vv-left', `${viewport.offsetLeft}px`);
      style.setProperty('--vv-width', `${viewport.width}px`);
      style.setProperty('--vv-centre', `${viewport.offsetLeft + viewport.width / 2}px`);
      style.setProperty('--vv-bottom', `${Math.max(0, layoutHeight - viewport.offsetTop - viewport.height)}px`);
    };

    this.zone.runOutsideAngular(() => {
      update();
      viewport.addEventListener('resize', update);
      viewport.addEventListener('scroll', update);
    });

    this.destroyRef.onDestroy(() => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    });
  }
}
